import { FC } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Card } from '../ui/card'
import { Check, Crown, Zap } from 'lucide-react'
import { SubscriptionTypeEnum } from '../../lib/enums'
import { cn } from '../../lib/utils'

interface PlanComparisonDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentTier: SubscriptionTypeEnum
  onSelectPlan: (tier: SubscriptionTypeEnum) => void
}

interface PlanFeature {
  text: string
  included: boolean
}

interface PlanDetails {
  name: string
  tier: SubscriptionTypeEnum
  price: string
  priceMonthly: number
  description: string
  icon: React.ReactNode
  color: string
  features: PlanFeature[]
  highlight?: boolean
  comingSoon?: boolean
}

const plans: PlanDetails[] = [
  {
    name: 'Basic',
    tier: SubscriptionTypeEnum.Basic,
    price: '$9.99',
    priceMonthly: 9.99,
    description: 'Perfect for getting started',
    icon: <Zap className="w-5 h-5" />,
    color: 'border-neutral-300 dark:border-neutral-700',
    features: [
      { text: 'Up to 200 inventory items', included: true },
      { text: 'Inventory tracking', included: true },
      { text: 'Units & bins organization', included: true },
      { text: 'Listing management', included: true },
      { text: 'Sales tracking', included: true },
      { text: 'Earnings dashboard', included: true },
      { text: 'Manual barcode entry', included: true },
      { text: 'Community support', included: true },
      { text: 'Barcode generation & printing', included: false },
      { text: 'Export capabilities', included: false },
    ],
  },
  {
    name: 'Premium',
    tier: SubscriptionTypeEnum.Premium,
    price: '$19.99',
    priceMonthly: 19.99,
    description: 'Best for growing businesses',
    icon: <Crown className="w-5 h-5" />,
    color: 'border-sumoPrimary',
    highlight: true,
    features: [
      { text: 'Everything in Basic', included: true },
      { text: 'Up to 1000 inventory items', included: true },
      { text: 'Barcode generation & printing', included: true },
      { text: 'Export inventory CSV', included: true },
      { text: 'Advanced reporting', included: true },
      { text: 'Priority support', included: true },
      { text: 'Enhanced analytics', included: true },
      { text: 'Multi-user access', included: false },
      { text: 'API access', included: false },
    ],
  },
  {
    name: 'Enterprise',
    tier: SubscriptionTypeEnum.Enterprise,
    price: '$49.99',
    priceMonthly: 49.99,
    description: 'For large-scale operations',
    icon: <Crown className="w-5 h-5" />,
    color: 'border-sumoAmber',
    comingSoon: true,
    features: [
      { text: 'Everything in Premium', included: true },
      { text: 'Unlimited inventory items', included: true },
      { text: 'Multi-user access', included: true },
      { text: 'API access', included: true },
      { text: 'Custom integrations', included: true },
      { text: 'Additional stores ($5/store)', included: true },
      { text: 'Custom CSV reporting', included: true },
      { text: 'Dedicated support', included: true },
      { text: 'Custom training', included: true },
      { text: 'White-label options', included: true },
    ],
  },
]

const PlanComparisonDialog: FC<PlanComparisonDialogProps> = ({
  open,
  onOpenChange,
  currentTier,
  onSelectPlan,
}) => {
  const currentPlan = plans.find((p) => p.tier === currentTier)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Compare Plans</DialogTitle>
          <DialogDescription>
            Choose the plan that's right for your business
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
          {plans.map((plan) => {
            const isCurrent = plan.tier === currentTier
            const isUpgrade = plan.priceMonthly > (currentPlan?.priceMonthly || 0)
            const isDowngrade = plan.priceMonthly < (currentPlan?.priceMonthly || 0)

            return (
              <Card
                key={plan.tier}
                className={cn(
                  'p-6 relative',
                  plan.highlight && 'border-2',
                  plan.color,
                  isCurrent && 'bg-neutral-50 dark:bg-neutral-900',
                  plan.comingSoon && 'opacity-60'
                )}
              >
                {/* Current Plan Badge */}
                {isCurrent && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-sumoPrimary text-sumoNavy">
                    Current Plan
                  </Badge>
                )}

                {/* Most Popular Badge */}
                {plan.highlight && !isCurrent && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-sumoPrimary to-sumoAmber text-sumoNavy">
                    Most Popular
                  </Badge>
                )}

                {/* Coming Soon Badge */}
                {plan.comingSoon && !isCurrent && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-neutral-500 text-white">
                    Coming Soon
                  </Badge>
                )}

                {/* Plan Header */}
                <div className="text-center mb-6">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <div className={cn(
                      'p-2 rounded-full',
                      plan.tier === SubscriptionTypeEnum.Basic
                        ? 'bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300'
                        : 'bg-sumoPrimary/10 text-sumoPrimary'
                    )}>
                      {plan.icon}
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                    {plan.name}
                  </h3>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
                    {plan.description}
                  </p>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-neutral-900 dark:text-neutral-100">
                      {plan.price}
                    </span>
                    <span className="text-neutral-600 dark:text-neutral-400">/month</span>
                  </div>
                </div>

                {/* Features List */}
                <div className="space-y-3 mb-6 min-h-[300px]">
                  {plan.features.map((feature, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-2 text-sm"
                    >
                      <div className={cn(
                        'mt-0.5 rounded-full p-0.5',
                        feature.included
                          ? 'bg-success500/10'
                          : 'bg-neutral-200 dark:bg-neutral-700'
                      )}>
                        <Check
                          className={cn(
                            'w-3 h-3',
                            feature.included
                              ? 'text-success500'
                              : 'text-neutral-400 dark:text-neutral-600'
                          )}
                        />
                      </div>
                      <span className={cn(
                        feature.included
                          ? 'text-neutral-700 dark:text-neutral-300'
                          : 'text-neutral-400 dark:text-neutral-600'
                      )}>
                        {feature.text}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Action Button */}
                <Button
                  onClick={() => onSelectPlan(plan.tier)}
                  disabled={isCurrent || plan.comingSoon}
                  variant={
                    isCurrent || plan.comingSoon
                      ? 'outline'
                      : plan.highlight
                      ? 'primary'
                      : 'outline'
                  }
                  className="w-full"
                >
                  {isCurrent
                    ? 'Current Plan'
                    : plan.comingSoon
                    ? 'Coming Soon'
                    : isUpgrade
                    ? `Upgrade to ${plan.name}`
                    : isDowngrade
                    ? `Downgrade to ${plan.name}`
                    : `Select ${plan.name}`}
                </Button>

                {/* Savings Badge */}
                {!isCurrent && currentPlan && (
                  <div className="mt-3 text-center text-xs">
                    {isUpgrade && (
                      <span className="text-neutral-600 dark:text-neutral-400">
                        +${(plan.priceMonthly - currentPlan.priceMonthly).toFixed(2)}/month
                      </span>
                    )}
                    {isDowngrade && (
                      <span className="text-success500">
                        Save ${(currentPlan.priceMonthly - plan.priceMonthly).toFixed(2)}/month
                      </span>
                    )}
                  </div>
                )}
              </Card>
            )
          })}
        </div>

        {/* Footer Note */}
        <div className="mt-6 p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
          <p className="text-sm text-neutral-600 dark:text-neutral-400 text-center">
            Changes to your plan will be prorated. You can upgrade, downgrade, or cancel anytime.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default PlanComparisonDialog
